<template>
  <v-card
    id="openings"
    :border="isSpecialist || dialog"
    flat
    tile
  >
    <v-card-title class="tab-title d-flex align-center">
      <v-row>
        <v-col class="d-flex align-center">
          <StandardButton
            v-if="showProviderProfileNavBtn"
            @click="emit('toggleProviderProfileNav')"
            :aria-label="$bct('Toggle provider profile navigation')"
            data-cy="provider_profile_nav_icon"
            icon="menu_open"
            variant="text"
          />
          <h2 class="v-card-title py-0 px-0 d-flex align-center">
            {{ $bct('Openings') }}
          </h2>
        </v-col>

        <v-col
          v-if="dialog"
          class="d-flex justify-end"
        >
          <StandardButton
            @click="$emit('close')"
            :aria-label="$bct('Close dialog')"
            icon="close"
            variant="text"
          />
        </v-col>
      </v-row>
    </v-card-title>

    <v-divider />

    <v-card-text class="mb-0">
      <div class="mb-3">
        <span>
          {{ $bct(ADD_OPENINGS_MESSAGE) }}
        </span>
      </div>

      <v-row
        v-if="programs.length > 0"
        class="d-flex align-center"
      >
        <v-col>
          <StandardChip
            v-if="openingsConfirmedAt && !anyOpenings"
            class="me-2"
            color="error"
            prepend-icon="cancel"
            size="default"
            text="No available openings"
          />

          <StandardChip
            v-if="openingsConfirmedAt"
            prepend-icon="info"
            size="default"
          >
            <template #append>
              <LongDate
                :date="openingsConfirmedAt"
                prefix="Last updated"
              />
            </template>
          </StandardChip>

          <StandardButton
            @click="$emit('change:openings-confirmed', 'Confirmed')"
            :ripple="false"
            class="fs-18"
            data-testid="openings-confirmed-button"
            role="link"
            text="Openings shown are up to date"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-for="opening in openings"
          v-show="opening.id || opening._isNew"
          :key="opening.id || opening._tempId"
          cols="12"
          lg="6"
        >
          <ProgramOpeningLegacy
            @destroy="destroy(opening)"
            @edit="edit(opening)"
            @update-count="updateCount(opening, $event)"
            ref="opening"
            :opening="opening"
            :processing="processing"
            :programs="programs"
            :provider-id="providerId"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider />

    <v-card-actions class="flex-wrap">
      <StandardButton
        v-if="programs.length > 0"
        @click="add()"
        :full-width="$vuetify.display.mdAndDown"
        :ripple="false"
        data-testid="add_opening_button"
        role="create"
        text="Add opening"
      />
      <StandardButton
        v-else-if="dialog && isManager"
        @click="goToProviderProfileProgramsTab"
        :full-width="$vuetify.display.mdAndDown"
        :text="`Edit my ${terms.programs.toLowerCase()}`"
        data-testid="go_to_provider_profile_button"
        role="secondary"
      />

      <v-spacer />

      <StandardButton
        v-if="anyOpenings || !openingsConfirmedAt"
        @click="closeAllOpenings()"
        :class="{ 'mt-2': $vuetify.display.mdAndDown }"
        :full-width="$vuetify.display.mdAndDown"
        :ripple="false"
        data-testid="remove_all_openings"
        role="secondary"
        text="I don't have any openings"
      />

      <StandardButton
        v-if="programs.length > 0 && dialog"
        @click="save"
        :class="{ 'mt-2': $vuetify.display.mdAndDown }"
        :disabled="!changed"
        :full-width="$vuetify.display.mdAndDown"
        data-testid="save_openings_button"
        text="Save"
      />
    </v-card-actions>

    <ResourceDialog
      @save="updateOrCreate"
      ref="createDialog"
      :max-width="600"
      title="Add openings"
    >
      <template
        v-if="draft"
        #form
      >
        <v-row>
          <LabeledTextfield
            v-model.number="draft.count"
            cols="12"
            data-cy="openings_input"
            message="# of openings"
            min="1"
            step="1"
            type="number"
          />
          <LabeledDatePicker
            v-model="draft.start_date"
            cols="12"
            message="Date available"
          />
          <LabeledSelect
            v-model="draft.programs"
            @change="updatePrograms"
            @change:clear-all="updatePrograms([])"
            @change:select-all="updatePrograms(programs.map((program) => program.id))"
            :disabled="processing"
            :items="programs"
            :message="`For which ${terms.programs.toLowerCase()}?`"
            :multiple="true"
            cols="12"
            data-cy="select_programs_input"
            item-title="name"
            item-value="id"
            select-all
          />
          <LabeledAgeMonthsYears
            v-if="draft.programs.length > 0"
            v-model="draft.age_min"
            cols="12"
            message="Minimum age for opening"
            min="0"
          />
          <LabeledAgeMonthsYears
            v-if="draft.programs.length > 0"
            v-model="draft.age_max"
            cols="12"
            message="Maximum age for opening"
            min="1"
          />
        </v-row>
      </template>
    </ResourceDialog>
  </v-card>
</template>

<script setup>
import _ from 'lodash';
import { useRoute, useRouter } from 'vue-router';

import LabeledAgeMonthsYears from '@/shared/components/form/LabeledAgeMonthsYears.vue';
import LabeledDatePicker from '@/shared/components/form/LabeledDatePicker.vue';
import LabeledSelect from '@/shared/components/form/LabeledSelect.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import LongDate from '@/shared/components/LongDate.vue';
import ProgramOpeningLegacy from '@/shared/components/provider/ProgramOpeningLegacy.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';
import useTerms from '@/shared/composables/useTerms';

const ADD_OPENINGS_MESSAGE =
  'Add openings to help families find your school. Families will get an update when any ' +
  'of the providers they have saved in the search portal add or increase their availability.';

const { api: Api } = useApi();
const eventBus = useEventBus();
const { terms } = useTerms();
const { isManager, isSpecialist } = useRole();
const route = useRoute();
const router = useRouter();
const $bct = inject('translator');

const props = defineProps({
  dialog: {
    type: Boolean,
    default: false,
  },
  openingsConfirmedAt: {
    type: String,
    default: null,
  },
  providerId: {
    type: String,
    default: null,
  },
  subsidyProgramId: {
    type: String,
    default: null,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits([
  'change',
  'change:openings-confirmed',
  'change:openings-count',
  'close',
  'destroy',
  'progress',
  'toggleProviderProfileNav',
]);

const changed = ref(false);
const createDialog = ref(null);
const draft = ref(null);
const openings = ref([]);
const originalOpenings = ref([]);
const processing = ref(false);
const programs = ref([]);

const anyOpenings = computed(() => {
  return openings.value.length > 0 && openings.value.some((opening) => opening.count > 0);
});

watch(changed, (newValue) => {
  if (newValue) emit('change');
});

watch(
  () => props.providerId,
  async () => {
    await load();
  },
  { immediate: true },
);

watch(
  () => openings.value.length,
  () => {
    emit('change:openings-count', openings.value.length);
    emit('progress', props.openingsConfirmedAt || openings.value.length > 0 ? 100 : 0);
  },
);

watch(
  () => props.openingsConfirmedAt,
  () => {
    if (props.openingsConfirmedAt) {
      emit('progress', props.openingsConfirmedAt || openings.value.length > 0 ? 100 : 0);
    }
  },
  { immediate: true },
);

watch(
  () => route.query.tab,
  async (newVal) => {
    if (newVal === 'openings') {
      await load();
    }
  },
);

function add() {
  draft.value = {
    count: 1,
    programs: [],
    provider_id: props.providerId,
    start_date: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }),
  };
  createDialog.value?.open();
}

function closeAllOpenings() {
  openings.value = openings.value.map((opening) => ({ ...opening, count: 0 }));
  changed.value = true;
  emit('change');
}

function create() {
  openings.value.push({
    ...draft.value,
    id: null,
    _isNew: true,
    _tempId: `temp_${Date.now()}`,
  });
  createDialog.value?.close();
  changed.value = true;
}

function destroy(opening) {
  openings.value = openings.value.filter((existing) =>
    opening.id ? existing.id !== opening.id : existing._tempId !== opening._tempId,
  );
  changed.value = true;
}

function edit(opening) {
  draft.value = JSON.parse(JSON.stringify(opening));
  createDialog.value?.open();
}

async function load() {
  await loadPrograms();
  loadOpenings();
}

function filterOpeningsForPrograms(loadedOpenings) {
  if (!props.subsidyProgramId) return loadedOpenings;

  const programIds = programs.value.map((program) => program.id);
  return loadedOpenings.filter((opening) =>
    (opening.programs || []).some((programId) => programIds.includes(programId)),
  );
}

async function goToProviderProfileProgramsTab() {
  await router.push({
    name: 'ProviderShow',
    params: { providerId: props.providerId },
    query: { tab: 'programs', backTo: 'ProviderIndex' },
  });
}

function loadOpenings() {
  Api.provider.opening.index(props.providerId, (resp) => {
    const loadedOpenings = filterOpeningsForPrograms(JSON.parse(JSON.stringify(resp.data)));
    originalOpenings.value = loadedOpenings;
    openings.value = JSON.parse(JSON.stringify(loadedOpenings));
    changed.value = false;
    processing.value = false;
  });
}

async function loadPrograms() {
  const filters = {};
  if (props.subsidyProgramId) filters.subsidy_program_id = props.subsidyProgramId;

  const { data } = await Api.program.index(props.providerId, filters);
  programs.value = data;
}

async function save() {
  if (!changed.value) return;

  processing.value = true;

  const toDelete = originalOpenings.value.filter(
    (original) => !openings.value.find((current) => current.id === original.id),
  );
  const toCreate = openings.value.filter((opening) => opening._isNew);
  const toUpdate = openings.value.filter((opening) => opening.id && !opening._isNew);

  try {
    const results = await Promise.all([
      ...toDelete.map((opening) =>
        Api.provider.opening.destroy(opening.provider_id || props.providerId, opening.id),
      ),
      ...toCreate.map((opening) =>
        Api.provider.opening.create(props.providerId, _.omit(opening, ['_isNew', '_tempId'])),
      ),
      ...toUpdate.map((opening) =>
        Api.provider.opening.update(opening.provider_id, opening.id, opening),
      ),
    ]);

    const allSucceeded = results.every(
      (response) => response?.status >= 200 && response?.status < 300,
    );

    if (allSucceeded) {
      loadOpenings();
      eventBus.chime('Openings updated');
    } else {
      eventBus.chime('Some openings failed to save. Please try again.');
    }
  } finally {
    processing.value = false;
  }
}

function setMinMaxDefaults() {
  const selectedPrograms = programs.value.filter((program) =>
    draft.value.programs.includes(program.id),
  );
  draft.value.age_min = Math.min(...selectedPrograms.map((program) => program.age_min));
  draft.value.age_max = Math.max(...selectedPrograms.map((program) => program.age_max));
}

function updateCount(opening, count) {
  const index = openings.value.findIndex((existing) =>
    opening.id ? existing.id === opening.id : existing._tempId === opening._tempId,
  );
  if (index !== -1) openings.value[index] = { ...openings.value[index], count };
  changed.value = true;
}

function updateOpening(opening) {
  const index = openings.value.findIndex((existing) =>
    opening.id ? existing.id === opening.id : existing._tempId === opening._tempId,
  );
  if (index !== -1) openings.value[index] = { ...opening };
  createDialog.value?.close();
  changed.value = true;
}

function updateOrCreate() {
  if (draft.value.id || draft.value._isNew) {
    updateOpening(draft.value);
  } else {
    create();
  }
}

function updatePrograms(newVal) {
  if (newVal) {
    draft.value.programs = newVal;
  }
  setMinMaxDefaults();
}

// While in a shared component state for saveBar (specialist) provider editing flow,
// and the back/next (manager/provider) flow we have to expose both load and save for
// ProviderShow access
defineExpose({ load, save });
</script>
